import { ConfluenceWorkspaceAri } from '@atlassian/ari/confluence';

import { buildContentAri, type BylineContentType } from '../utils/contentAri.ts';
import { buildRelatedContentCypher } from '../utils/relatedContentRelationships.ts';

import type {
	preloadRelatedContentSampleQuery$data,
	preloadRelatedContentSampleQuery$variables,
} from './__generated__/preloadRelatedContentSampleQuery.graphql.ts';

export type RelatedContentSsrStatus = 'present' | 'empty' | 'unavailable';

// `count` (sampled rows, at most `RELATED_CONTENT_SAMPLE_LIMIT`) exists only on the 'present' variant
// so the SSR placeholder can add it to the other sources when deciding whether the page reaches the
// byline's row minimum. CCINT-9359
export type RelatedContentSsrState = Readonly<
	| { contentId: string; status: 'present'; count: number }
	| { contentId: string; status: 'empty' | 'unavailable' }
>;

export type RelatedContentSampleUnavailableReason =
	| 'invalid-input'
	| 'malformed-response'
	| 'query-error';

// Only the row count leaves this layer; the cached query holds the entity ARIs like any other
// preloaded query.
export type RelatedContentSampleResult =
	| Readonly<{ status: 'available'; count: number }>
	| Readonly<{ status: 'unavailable'; reason: RelatedContentSampleUnavailableReason }>;

// The byline hides below three rows, so three is all the placeholder needs to know about. Each graph
// edge is capped at this many rows and `first` caps the result as a whole.
export const RELATED_CONTENT_SAMPLE_LIMIT = 3;

const RELATED_CONTENT_COLUMN = 'relatedContent';

export function createRelatedContentSsrState(
	contentId: string,
	status: 'empty' | 'unavailable',
): RelatedContentSsrState;
export function createRelatedContentSsrState(
	contentId: string,
	status: 'present',
	count: number,
): RelatedContentSsrState;
export function createRelatedContentSsrState(
	contentId: string,
	status: RelatedContentSsrStatus,
	count?: number,
): RelatedContentSsrState {
	if (status === 'present') {
		return Object.freeze({ contentId, status, count: count ?? 0 });
	}
	return Object.freeze({ contentId, status });
}

// Shared by the preloader (network) and the store-only reader so both key the store on a
// byte-identical query and variables. Returns `undefined` for input that must not be queried, which
// the reader treats as "skip" and the preloader treats as "invalid-input". Pages and blog posts live
// under distinct graph ARIs, so `contentType` (undefined means page) is part of that key.
export const buildRelatedContentSampleVariables = (
	cloudId: string,
	activationId: string,
	contentId: string,
	contentType?: BylineContentType,
): preloadRelatedContentSampleQuery$variables | undefined => {
	if (!cloudId.trim() || !activationId.trim() || !/^[1-9]\d*$/.test(contentId)) {
		return undefined;
	}

	let pageAri: string;
	let queryContext: string;
	try {
		pageAri = buildContentAri(cloudId, contentId, contentType);
		queryContext = ConfluenceWorkspaceAri.create({ siteId: cloudId, activationId }).toString();
	} catch {
		return undefined;
	}

	// Same edges, context and graph version as the client's `useRelatedContent`, capped per edge.
	return {
		cypherQuery: buildRelatedContentCypher(pageAri, RELATED_CONTENT_SAMPLE_LIMIT),
		queryContext,
		version: 'V3',
		first: RELATED_CONTENT_SAMPLE_LIMIT,
	};
};

// Pure normalization of the bounded sample. Defensive against a malformed shape because the response
// is an untyped Cypher projection. Only the row count leaves this function.
export const normalizeRelatedContentSampleData = (
	data: preloadRelatedContentSampleQuery$data | null | undefined,
): RelatedContentSampleResult => {
	const edges = data?.graphStore_cypherQueryV2?.edges;
	if (!edges || edges.length > RELATED_CONTENT_SAMPLE_LIMIT) {
		return { status: 'unavailable', reason: 'malformed-response' };
	}

	for (const edge of edges) {
		const columns = edge.node.columns;
		if (columns.length !== 1) {
			return { status: 'unavailable', reason: 'malformed-response' };
		}

		const column = columns[0];
		if (
			column.key !== RELATED_CONTENT_COLUMN ||
			column.value?.__typename !== 'GraphStoreCypherQueryV2AriNode' ||
			typeof column.value.id !== 'string' ||
			!column.value.id.startsWith('ari:')
		) {
			return { status: 'unavailable', reason: 'malformed-response' };
		}
	}

	return { status: 'available', count: edges.length };
};

// Maps a query response (from the network preload or the hydrated cache) to the terminal SSR state.
export const normalizeRelatedContentSsrState = (
	contentId: string,
	data: preloadRelatedContentSampleQuery$data | null | undefined,
): RelatedContentSsrState => {
	const result = normalizeRelatedContentSampleData(data);
	if (result.status === 'unavailable') {
		return createRelatedContentSsrState(contentId, 'unavailable');
	}
	return result.count > 0
		? createRelatedContentSsrState(contentId, 'present', result.count)
		: createRelatedContentSsrState(contentId, 'empty');
};
